import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Color per discipline (agencies handled separately in a distinct color).
const DISCIPLINE_COLORS = {
  PT: '#2563eb',
  PTA: '#60a5fa',
  OT: '#16a34a',
  COTA: '#86efac',
  SLP: '#9333ea',
  ST: '#9333ea',
  Administrative: '#f59e0b',
  Admin: '#f59e0b',
  Other: '#94a3b8',
};
const AGENCY_COLOR = '#111827';

const colorFor = (discipline) => DISCIPLINE_COLORS[discipline] || DISCIPLINE_COLORS.Other;

function FitBounds({ points }) {
  const map = useMap();
  const done = useRef(false);
  useEffect(() => {
    if (!done.current && points.length > 0) {
      done.current = true;
      try {
        map.fitBounds(points.map((p) => [p.lat, p.lng]), { padding: [40, 40], maxZoom: 11 });
      } catch (e) { /* single point */ }
    }
  }, [points, map]);
  return null;
}

export const DashboardMap = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ personnel: [], agencies: [] });
  const [loading, setLoading] = useState(true);
  const [refining, setRefining] = useState(0);
  const [showPersonnel, setShowPersonnel] = useState(true);
  const [showAgencies, setShowAgencies] = useState(true);
  const refetchTimer = useRef(null);

  const load = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/dashboard/map-data`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData({ personnel: res.data.personnel || [], agencies: res.data.agencies || [] });
      setRefining(res.data.refining || 0);
    } catch (e) {
      console.error('Failed to load map data', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    return () => { if (refetchTimer.current) clearTimeout(refetchTimer.current); };
  }, []);

  // Progressively refine addresses that weren't yet geocoded (cache fills over calls).
  useEffect(() => {
    if (refining > 0) {
      refetchTimer.current = setTimeout(load, 2000);
    }
    return () => { if (refetchTimer.current) clearTimeout(refetchTimer.current); };
  }, [refining]);

  const allPoints = useMemo(() => {
    const pts = [];
    if (showPersonnel) pts.push(...data.personnel);
    if (showAgencies) pts.push(...data.agencies);
    return pts;
  }, [data, showPersonnel, showAgencies]);

  const disciplinesPresent = useMemo(() => {
    return Array.from(new Set(data.personnel.map((p) => p.discipline))).sort();
  }, [data.personnel]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[520px] rounded-lg border bg-muted/20" data-testid="dashboard-map-loading">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const center = allPoints.length ? [allPoints[0].lat, allPoints[0].lng] : [39.5, -98.35];

  return (
    <div className="space-y-3" data-testid="dashboard-map">
      {/* Legend + toggles */}
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <button
          onClick={() => setShowPersonnel((v) => !v)}
          className={`px-3 py-1 rounded-full border transition-colors ${showPersonnel ? 'bg-primary/10 border-primary text-primary' : 'bg-muted text-muted-foreground'}`}
          data-testid="toggle-personnel-layer"
        >
          Personnel ({data.personnel.length})
        </button>
        <button
          onClick={() => setShowAgencies((v) => !v)}
          className={`px-3 py-1 rounded-full border transition-colors ${showAgencies ? 'border-gray-800 text-gray-900 bg-gray-100' : 'bg-muted text-muted-foreground'}`}
          data-testid="toggle-agencies-layer"
        >
          Agencies ({data.agencies.length})
        </button>
        <span className="mx-1 h-4 w-px bg-border" />
        {disciplinesPresent.map((d) => (
          <span key={d} className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: colorFor(d) }} />
            {d}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full inline-block border border-white" style={{ backgroundColor: AGENCY_COLOR }} />
          Agency
        </span>
        {refining > 0 && (
          <span className="inline-flex items-center gap-1.5 text-muted-foreground" data-testid="map-refining-indicator">
            <Loader2 className="w-3 h-3 animate-spin" /> refining addresses…
          </span>
        )}
      </div>

      {allPoints.length === 0 ? (
        <div className="flex items-center justify-center h-[480px] rounded-lg border bg-muted/20 text-sm text-muted-foreground text-center px-6" data-testid="dashboard-map-empty">
          No mappable locations yet. Add an address (or ZIP) to personnel and agency records to see them here.
        </div>
      ) : (
        <div className="rounded-lg overflow-hidden border" style={{ height: 520 }}>
          <MapContainer center={center} zoom={5} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
            <TileLayer
              attribution='&copy; OpenStreetMap contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds points={allPoints} />
            {showPersonnel && data.personnel.map((p) => (
              <CircleMarker
                key={`p-${p.id}`}
                center={[p.lat, p.lng]}
                radius={7}
                pathOptions={{ color: '#fff', weight: 2, fillColor: colorFor(p.discipline), fillOpacity: 1 }}
                eventHandlers={{ click: () => navigate(`/personnel/${p.id}`) }}
              >
                <Tooltip permanent direction="top" offset={[0, -8]} className="map-name-label">
                  {p.name}
                </Tooltip>
              </CircleMarker>
            ))}
            {showAgencies && data.agencies.map((a) => (
              <CircleMarker
                key={`a-${a.id}`}
                center={[a.lat, a.lng]}
                radius={8}
                pathOptions={{ color: '#fff', weight: 2, fillColor: AGENCY_COLOR, fillOpacity: 1 }}
                eventHandlers={{ click: () => navigate(`/agencies/${a.id}`) }}
              >
                <Tooltip permanent direction="top" offset={[0, -9]} className="map-name-label map-name-label-agency">
                  {a.name}
                </Tooltip>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
      )}
    </div>
  );
};
